import type { CountryIso } from './country';
import { isBG, isCS, isHU, isPL, type LanguageIso } from './language';

export const LOCALES_ISO = {
  en: 'en',
  'bg-BG': 'bg_BG',
  'cs-CZ': 'cs_CZ',
  'da-DK': 'da_DK',
  'de-DE': 'de_DE',
  'de-AT': 'de_AT',
  'de-CH': 'de_CH',
  'de-LU': 'de_LU',
  'en-GB': 'en_GB',
  'en-IE': 'en_IE',
  'es-ES': 'es_ES',
  'fi-FI': 'fi_FI',
  'fr-FR': 'fr_FR',
  'fr-CH': 'fr_CH',
  'fr-BE': 'fr_BE',
  'hu-HU': 'hu_HU',
  'it-IT': 'it_IT',
  'it-CH': 'it_CH',
  'nl-NL': 'nl_NL',
  'nl-BE': 'nl_BE',
  'pl-PL': 'pl_PL',
  'pt-PT': 'pt_PT',
  'sv-SE': 'sv_SE'
};

export type LocaleIso = keyof typeof LOCALES_ISO;

export const TIME_ZONE_BY_LOCALE: Record<LocaleIso, string> = {
  en: 'Europe/London',
  'bg-BG': 'Europe/Sofia',
  'cs-CZ': 'Europe/Prague',
  'da-DK': 'Europe/Copenhagen',
  'de-DE': 'Europe/Berlin',
  'de-AT': 'Europe/Vienna',
  'de-CH': 'Europe/Zurich',
  'de-LU': 'Europe/Luxembourg',
  'en-GB': 'Europe/London',
  'en-IE': 'Europe/Dublin',
  'es-ES': 'Europe/Madrid',
  'fi-FI': 'Europe/Helsinki',
  'fr-FR': 'Europe/Paris',
  'fr-CH': 'Europe/Zurich',
  'fr-BE': 'Europe/Brussels',
  'hu-HU': 'Europe/Budapest',
  'it-IT': 'Europe/Rome',
  'it-CH': 'Europe/Zurich',
  'nl-NL': 'Europe/Amsterdam',
  'nl-BE': 'Europe/Brussels',
  'pl-PL': 'Europe/Warsaw',
  'pt-PT': 'Europe/Lisbon',
  'sv-SE': 'Europe/Stockholm'
};

export function getTimeZone(locale: LocaleIso): string {
  return TIME_ZONE_BY_LOCALE[locale];
}

export interface LocaleParts {
  countryCode: CountryIso;
  languageCode: LanguageIso;
}

/**
 * Locale fallback mappings for product reviews
 *
 * Some locales don't have localized reviews, so they fall back to en-GB
 */
export const REVIEWS_LOCALE_FALLBACK_MAP: Record<string, LocaleIso> = {
  'pl-PL': 'en-GB',
  'hu-HU': 'en-GB',
  'bg-BG': 'en-GB',
  'cs-CZ': 'en-GB'
};

export const HIDDEN_LOCALES = ['en'];

export function isHiddenLocale(locale: string): boolean {
  return HIDDEN_LOCALES.includes(locale);
}

// New Markets project is paused (PXMCY-37015); remove from here once it resumes
export function isPausedNewMarketLocale(locale: string): boolean {
  return isPL(locale) || isHU(locale) || isCS(locale) || isBG(locale);
}

export function getLocale(country: CountryIso, language: LanguageIso, separator: '_' | '-' = '-'): string {
  return `${language}${separator}${country}`;
}

export function getValidLocaleISO(envLocale: string | undefined): LocaleIso {
  if (envLocale && envLocale in LOCALES_ISO) {
    return envLocale as LocaleIso;
  }

  return 'de-DE'; // default fallback
}

/**
 * Converts a locale string (e.g., 'de-DE') into an object containing separate country and language codes.
 *
 * @param locale - The locale string to split, formatted as 'language-country' (e.g., 'de-DE') or just 'language' for base locales (e.g., 'en').
 * @returns An object with `countryCode` and `languageCode` properties. For base locales without country, countryCode will be empty string.
 */
export function getLocaleParts(locale: LocaleIso): LocaleParts {
  const [language, country = ''] = locale.split('-');

  return {
    countryCode: country as CountryIso, // e.g. 'DE', or '' for base locales
    languageCode: language as LanguageIso // e.g. 'de'
  };
}
